/**
 * SectionHeader (docs/design/MASTER.md sections 2, 5 and 6): a hairline, the numbered mono eyebrow
 * ("01 / Mechanism"), the H2, an optional one-line summary and an optional action link on the right.
 * The same opener as the research bench and nootropics pages, on the shared type scale.
 *
 * Spacing follows MASTER section 5: eyebrow to title 8px, title to summary 12px, header to content 24px.
 */
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

/** Two-digit section numbers: 1 reads "01". Strings pass through. */
export function sectionNumber(no: number | string): string {
  return typeof no === "number" ? String(no).padStart(2, "0") : no;
}

export interface SectionActionLink {
  label: string;
  /** Internal paths ("/compare") use the router; anything else opens as a normal link. */
  href: string;
}

export interface SectionHeaderProps {
  /** The section's number in the page, matching OnThisPage. 1 renders "01". */
  no?: number | string;
  /** The short mono label after the number: "Mechanism", "Prices". */
  eyebrow: string;
  title: ReactNode;
  /** One plain sentence under the title (the first of the two layers, MASTER principle 7). */
  summary?: ReactNode;
  /** A right-aligned link ("All prices") or your own element. */
  action?: SectionActionLink | ReactNode;
  /** The heading's id, for OnThisPage and anchor links. */
  id?: string;
  as?: "h2" | "h3";
  /** The hairline above the eyebrow. Default true. */
  rule?: boolean;
  className?: string;
}

function isActionLink(a: unknown): a is SectionActionLink {
  return typeof a === "object" && a !== null && "href" in a && "label" in a;
}

export function SectionAction({ label, href }: SectionActionLink) {
  // 44px tall hit area, pulled back into the eyebrow row with negative margins so the row stays 24px.
  const cls =
    "-my-2.5 inline-flex min-h-11 shrink-0 items-center gap-1 type-small font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-control";
  const body = (
    <>
      {label}
      <ArrowRight aria-hidden className="h-4 w-4" />
    </>
  );
  return href.startsWith("/") ? (
    <Link to={href} className={cls}>
      {body}
    </Link>
  ) : (
    <a href={href} className={cls}>
      {body}
    </a>
  );
}

export function SectionHeader({ no, eyebrow, title, summary, action, id, as: Tag = "h2", rule = true, className }: SectionHeaderProps) {
  return (
    <div className={cn(rule && "border-t border-border pt-4 sm:pt-5", "mb-6", className)}>
      <div className="flex min-h-6 items-center justify-between gap-4">
        <p className="type-eyebrow text-muted-foreground">
          {no !== undefined && (
            <>
              <span className="text-foreground">{sectionNumber(no)}</span>
              <span aria-hidden className="mx-2 opacity-50">
                /
              </span>
            </>
          )}
          {eyebrow}
        </p>
        {action ? isActionLink(action) ? <SectionAction {...action} /> : action : null}
      </div>
      <Tag id={id} className={cn("mt-2 scroll-mt-24 text-foreground", Tag === "h2" ? "type-h2" : "type-h3")}>
        {title}
      </Tag>
      {summary && <p className="type-body mt-3 max-w-[70ch] text-muted-foreground">{summary}</p>}
    </div>
  );
}
